import React, { useState, useEffect } from "react";
import { ArrowLeftRight, X } from "lucide-react";
import { toast } from "sonner";

export const TransferModal = ({ isOpen, onClose, onSubmit, bankAccounts = [] }) => {
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    if (isOpen) {
      const active = bankAccounts.filter(b => b.is_active !== false);
      setFromId(active[0]?.id || "");
      setToId(active[1]?.id || "");
      setAmount("");
      setDescription("");
      setDate(new Date().toISOString().slice(0, 10));
    }
  }, [isOpen, bankAccounts]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!fromId || !toId) {
      toast.error("Selecione conta origem e destino.");
      return;
    }
    if (fromId === toId) {
      toast.error("Conta origem e destino não podem ser iguais.");
      return;
    }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Informe um valor válido.");
      return;
    }
    onSubmit({
      from_bank_account_id: fromId,
      to_bank_account_id: toId,
      amount: amt,
      description: description.trim(),
      date,
    });
  };

  const formatCurrency = (n) =>
    (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  const activeAccounts = bankAccounts.filter(b => b.is_active !== false);
  const fromAccount = activeAccounts.find(a => a.id === fromId);
  const toAccount = activeAccounts.find(a => a.id === toId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div
        data-testid="modal-transfer-form"
        className="w-full max-w-md bg-[#131C2E] border border-[#D4AF37]/40 rounded-2xl shadow-[0_0_50px_rgba(212,175,55,0.25)] overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-[#080C14]">
          <div className="flex items-center space-x-2">
            <ArrowLeftRight className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="font-serif font-bold text-lg text-white">Transferência entre Contas</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="btn-close-transfer-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs text-slate-300">

          <div>
            <label className="block mb-1 font-semibold text-slate-300">Conta Origem (Saída) *</label>
            <select
              data-testid="select-transfer-from"
              value={fromId}
              onChange={(e) => setFromId(e.target.value)}
              className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              required
            >
              {activeAccounts.map((ba) => (
                <option key={ba.id} value={ba.id}>
                  {ba.name} {ba.current_balance != null ? `— ${formatCurrency(ba.current_balance)}` : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-center py-1">
            <div className="w-9 h-9 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
              <ArrowLeftRight className="w-4 h-4 text-[#D4AF37]" />
            </div>
          </div>

          <div>
            <label className="block mb-1 font-semibold text-slate-300">Conta Destino (Entrada) *</label>
            <select
              data-testid="select-transfer-to"
              value={toId}
              onChange={(e) => setToId(e.target.value)}
              className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              required
            >
              {activeAccounts.map((ba) => (
                <option key={ba.id} value={ba.id} disabled={ba.id === fromId}>
                  {ba.name} {ba.current_balance != null ? `— ${formatCurrency(ba.current_balance)}` : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Valor (R$) *</label>
              <input
                data-testid="input-transfer-amount"
                type="number"
                step="0.01"
                min="0.01"
                required
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full bg-[#080C14] text-[#D4AF37] font-mono font-bold p-2.5 rounded-xl border border-slate-700 text-xs"
              />
            </div>
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Data</label>
              <input
                data-testid="input-transfer-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              />
            </div>
          </div>

          <div>
            <label className="block mb-1 font-semibold text-slate-300">Descrição / Motivo</label>
            <input
              data-testid="input-transfer-description"
              type="text"
              placeholder="Ex: Reserva para pagto de aluguel, aplicação de sobra..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
            />
          </div>

          {fromAccount && toAccount && amount && parseFloat(amount) > 0 && (
            <div className="p-3 rounded-xl bg-[#080C14] border border-[#D4AF37]/30 text-[11px]">
              <p className="text-slate-300">
                Ao confirmar, <span className="font-bold text-rose-400">R$ {formatCurrency(parseFloat(amount)).replace("R$", "").trim()}</span> sairão de{" "}
                <span className="font-semibold text-white">{fromAccount.name}</span> e entrarão em{" "}
                <span className="font-semibold text-white">{toAccount.name}</span>.
              </p>
              <p className="text-slate-500 mt-1 italic">Não contabilizado como receita/despesa nos relatórios de resultado.</p>
            </div>
          )}

          <div className="pt-4 border-t border-slate-800 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white font-semibold text-xs"
            >
              Cancelar
            </button>
            <button
              data-testid="btn-submit-transfer"
              type="submit"
              className="bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-6 py-2 rounded-xl text-xs uppercase shadow-[0_0_15px_rgba(212,175,55,0.3)]"
            >
              Transferir
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};
